import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import axios from "axios";
import { getAuthHeaders } from "./auth";

const STAGE_LABELS = {
  new: "New",
  contacted: "Contacted",
  qualified: "Qualified",
  demo: "Demo / trial",
  proposal: "Proposal",
  won: "Won",
  lost: "Lost",
};

const emptyLead = {
  id: "",
  companyName: "",
  contactName: "",
  email: "",
  phone: "",
  stage: "new",
  source: "",
  estimatedMrr: "",
  notes: "",
  nextFollowUpAt: "",
  lostReason: "",
  linkedUserId: "",
};

function formatShortDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function SuperAdminCrmPanel() {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [stageFilter, setStageFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [stages, setStages] = useState(Object.keys(STAGE_LABELS));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyLead);
  const [saving, setSaving] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await axios.get("/superadmin/crm/stats", { headers: getAuthHeaders() });
      setStats(data);
    } catch {
      setStats(null);
    }
  }, []);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get("/superadmin/crm/leads", {
        headers: getAuthHeaders(),
        params: {
          page: page + 1,
          limit: pageSize,
          stage: stageFilter || undefined,
          q: searchQuery.trim() || undefined,
        },
      });
      setLeads((data.items || []).map((r) => ({ ...r, id: r._id || r.id })));
      setTotal(data.total || 0);
      if (Array.isArray(data.stages) && data.stages.length) {
        setStages(data.stages);
      }
    } catch (e) {
      setError(e.response?.data?.message || "Could not load pipeline.");
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, stageFilter, searchQuery]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const applySearch = () => {
    setPage(0);
    setSearchQuery(searchInput);
  };

  const openNew = () => {
    setForm({ ...emptyLead, stage: "new" });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      companyName: row.companyName || "",
      contactName: row.contactName || "",
      email: row.email || "",
      phone: row.phone || "",
      stage: row.stage || "new",
      source: row.source || "",
      estimatedMrr: row.estimatedMrr != null ? String(row.estimatedMrr) : "",
      notes: row.notes || "",
      nextFollowUpAt: row.nextFollowUpAt ? new Date(row.nextFollowUpAt).toISOString().slice(0, 16) : "",
      lostReason: row.lostReason || "",
      linkedUserId: row.linkedUserId ? String(row.linkedUserId) : "",
    });
    setDialogOpen(true);
  };

  const saveLead = async () => {
    setSaving(true);
    setError("");
    if (!form.companyName.trim() && !form.email.trim()) {
      setError("Company name or email is required.");
      setSaving(false);
      return;
    }
    try {
      const mrr =
        form.estimatedMrr === "" || form.estimatedMrr == null ? null : Number(form.estimatedMrr);
      const payload = {
        companyName: form.companyName,
        contactName: form.contactName,
        email: form.email,
        phone: form.phone,
        stage: form.stage,
        source: form.source,
        estimatedMrr: Number.isFinite(mrr) ? mrr : null,
        notes: form.notes,
        nextFollowUpAt: form.nextFollowUpAt ? new Date(form.nextFollowUpAt).toISOString() : null,
        lostReason: form.lostReason,
        linkedUserId: form.linkedUserId || null,
      };
      if (form.id) {
        await axios.patch(`/superadmin/crm/leads/${form.id}`, payload, { headers: getAuthHeaders() });
      } else {
        await axios.post("/superadmin/crm/leads", payload, { headers: getAuthHeaders() });
      }
      setDialogOpen(false);
      await loadLeads();
      await loadStats();
    } catch (e) {
      setError(e.response?.data?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    setError("");
    try {
      await axios.delete(`/superadmin/crm/leads/${id}`, { headers: getAuthHeaders() });
      await loadLeads();
      await loadStats();
    } catch (e) {
      setError(e.response?.data?.message || "Delete failed.");
    }
  };

  const gridColumns = [
    { field: "companyName", headerName: "Company", flex: 1, minWidth: 140 },
    { field: "contactName", headerName: "Contact", width: 130 },
    { field: "email", headerName: "Email", flex: 1, minWidth: 180 },
    { field: "phone", headerName: "Phone", width: 120 },
    {
      field: "stage",
      headerName: "Stage",
      width: 140,
      valueGetter: (p) => STAGE_LABELS[p.row.stage] || p.row.stage,
    },
    {
      field: "estimatedMrr",
      headerName: "Est. MRR",
      width: 100,
      valueFormatter: (p) => (p.value != null && p.value !== "" ? `$${p.value}` : "—"),
    },
    {
      field: "nextFollowUpAt",
      headerName: "Follow up",
      width: 110,
      valueFormatter: (p) => formatShortDate(p.value),
    },
    {
      field: "actions",
      headerName: "",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => openEdit(params.row)} aria-label="Edit lead">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => deleteLead(params.row.id)} aria-label="Delete lead">
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 800 }}>
        Track Clibbins B2B prospects (lawn care operators) through your funnel. Stages are for your process — move leads as
        they progress toward a paid subscription.
      </Typography>

      {stats && stats.stages ? (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {stages.map((s) => (
            <Chip
              key={s}
              label={`${STAGE_LABELS[s] || s}: ${stats.stages[s] ?? 0}`}
              color={stageFilter === s ? "primary" : "default"}
              onClick={() => {
                setStageFilter(stageFilter === s ? "" : s);
                setPage(0);
              }}
              variant={stageFilter === s ? "filled" : "outlined"}
            />
          ))}
        </Box>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              size="small"
              label="Search company, contact, email…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              fullWidth
              onKeyDown={(e) => e.key === "Enter" && applySearch()}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              size="small"
              label="Stage filter"
              value={stageFilter}
              onChange={(e) => {
                setStageFilter(e.target.value);
                setPage(0);
              }}
              fullWidth
            >
              <MenuItem value="">All stages</MenuItem>
              {stages.map((s) => (
                <MenuItem key={s} value={s}>
                  {STAGE_LABELS[s] || s}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={5}>
            <Button variant="outlined" onClick={applySearch} sx={{ mr: 1 }}>
              Apply search
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
              Add lead
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ height: 520, width: "100%" }}>
        <DataGrid
          rows={leads}
          columns={gridColumns}
          loading={loading}
          pagination
          paginationMode="server"
          page={page}
          pageSize={pageSize}
          rowCount={total}
          onPageChange={(p) => setPage(p)}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          density="compact"
        />
      </Box>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? "Edit lead" : "New lead"}</DialogTitle>
        <DialogContent dividers>
          <TextField
            margin="dense"
            label="Company name"
            value={form.companyName}
            onChange={(e) => setForm((s) => ({ ...s, companyName: e.target.value }))}
            fullWidth
          />
          <TextField
            margin="dense"
            label="Contact name"
            value={form.contactName}
            onChange={(e) => setForm((s) => ({ ...s, contactName: e.target.value }))}
            fullWidth
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
            fullWidth
          />
          <TextField
            margin="dense"
            label="Phone"
            value={form.phone}
            onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))}
            fullWidth
          />
          <TextField
            margin="dense"
            select
            label="Stage"
            value={form.stage}
            onChange={(e) => setForm((s) => ({ ...s, stage: e.target.value }))}
            fullWidth
          >
            {stages.map((s) => (
              <MenuItem key={s} value={s}>
                {STAGE_LABELS[s] || s}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="Source (e.g. website, referral)"
            value={form.source}
            onChange={(e) => setForm((s) => ({ ...s, source: e.target.value }))}
            fullWidth
          />
          <TextField
            margin="dense"
            label="Estimated MRR ($/mo)"
            type="number"
            value={form.estimatedMrr}
            onChange={(e) => setForm((s) => ({ ...s, estimatedMrr: e.target.value }))}
            fullWidth
            inputProps={{ min: 0, step: 1 }}
          />
          <TextField
            margin="dense"
            label="Next follow-up"
            type="datetime-local"
            value={form.nextFollowUpAt}
            onChange={(e) => setForm((s) => ({ ...s, nextFollowUpAt: e.target.value }))}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            margin="dense"
            label="Notes"
            value={form.notes}
            onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
            fullWidth
            multiline
            minRows={3}
          />
          <TextField
            margin="dense"
            label="Lost reason (if lost)"
            value={form.lostReason}
            onChange={(e) => setForm((s) => ({ ...s, lostReason: e.target.value }))}
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            margin="dense"
            label="Linked operator User id (MongoDB)"
            value={form.linkedUserId}
            onChange={(e) => setForm((s) => ({ ...s, linkedUserId: e.target.value }))}
            fullWidth
            helperText="Optional — paste a user _id when they convert to a paying operator."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveLead} disabled={saving}>
            {saving ? <CircularProgress size={22} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
